import { listen, onCleanup } from './cleanup.js'

/* ///////////////////////////////////////////////////////////////////// */
/* ///////////////////////////// SUBMENU /////////////////////////////// */
/* ///////////////////////////////////////////////////////////////////// */

/** Padding extra bajo el submenu-list más alto */
const WRAPPER_EXTRA = 16

/**
 * Submenu desktop (hover + panel en wrapper) / mobile (acordeón).
 * @param {{ isDesktop: boolean }} device_data
 */
function submenu (device_data) {
  const triggers = Array.from(document.querySelectorAll('.m-item.submenu'))
    .filter(el => el.offsetParent !== null)

  if (!triggers.length) return

  const instances = triggers.map(trigger => {
    const list = trigger.querySelector('.submenu-list')
    return list ? { trigger, list } : null
  }).filter(Boolean)

  if (!instances.length) return

  if (device_data.isDesktop) {
    setupDesktop(instances)
  } else {
    setupMobile(instances)
  }
}

/**
 * @param {{ trigger: HTMLElement, list: HTMLElement }[]} instances
 */
function setupDesktop (instances) {
  const wrapper = document.querySelector('.submenu-wrapper')
  if (!wrapper) return

  const header = wrapper.closest('header')
  if (!header) return

  let closeTimer = null

  const getMaxListHeight = () =>
    instances.reduce((max, { list }) => Math.max(max, list.scrollHeight), 0)

  const getListTop = () => {
    const headerRect = header.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    return headerRect.bottom - wrapperRect.top
  }

  const expandWrapper = () => {
    const top = getListTop()
    wrapper.style.height = `${top + getMaxListHeight() + WRAPPER_EXTRA}px`
    wrapper.classList.add('is-open')
  }

  const collapseWrapper = () => {
    wrapper.style.height = '0px'
    wrapper.classList.remove('is-open')
  }

  const closeAll = () => {
    instances.forEach(({ list }) => list.classList.remove('is-open'))
  }

  const openList = (list, syncPosition) => {
    clearTimeout(closeTimer)
    closeAll()
    syncPosition()
    list.classList.add('is-open')
    expandWrapper()
  }

  const scheduleClose = () => {
    clearTimeout(closeTimer)
    closeTimer = setTimeout(() => {
      closeAll()
      collapseWrapper()
    }, 120)
  }

  instances.forEach(({ trigger, list }) => {
    list.classList.add('submenu-list--desktop')
    wrapper.appendChild(list)

    const syncPosition = () => {
      const wrapperRect = wrapper.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      const top = getListTop()-20; // -20px para que el submenu no tape el trigger
      const left = triggerRect.left - wrapperRect.left
      const width = `${triggerRect.width}px`

      list.style.setProperty('--submenu-top', `${top}px`)
      list.style.setProperty('--submenu-left', `${left}px`)
      list.style.setProperty('--submenu-item-width', width)
    }

    syncPosition()
    listen(window, 'resize', () => {
      syncPosition()
      if (wrapper.classList.contains('is-open')) expandWrapper()
    })

    listen(trigger, 'mouseenter', () => openList(list, syncPosition))
    listen(trigger, 'mouseleave', scheduleClose)
  })

  /* El wrapper mantiene el panel abierto al cruzar del trigger al listado */
  listen(wrapper, 'mouseenter', () => clearTimeout(closeTimer))
  listen(wrapper, 'mouseleave', scheduleClose)

  onCleanup(() => {
    clearTimeout(closeTimer)
    closeAll()
    collapseWrapper()
    instances.forEach(({ trigger, list }) => {
      list.classList.remove('is-open', 'submenu-list--desktop')
      list.style.removeProperty('--submenu-top')
      list.style.removeProperty('--submenu-left')
      list.style.removeProperty('--submenu-item-width')
      if (trigger.isConnected) {
        trigger.appendChild(list)
      }
    })
  })
}

/**
 * @param {{ trigger: HTMLElement, list: HTMLElement }[]} instances
 */
function setupMobile (instances) {
  instances.forEach(({ trigger, list }) => {
    list.classList.add('submenu-list--mobile')

    const link = trigger.querySelector(':scope > a')
    if (!link) return

    link.setAttribute('aria-expanded', 'false')

    const onClick = (e) => {
      e.preventDefault()
      const willOpen = !trigger.classList.contains('is-open')

      instances.forEach(({ trigger: t, list: l }) => {
        t.classList.remove('is-open')
        l.classList.remove('is-open')
        t.querySelector(':scope > a')?.setAttribute('aria-expanded', 'false')
      })

      if (willOpen) {
        trigger.classList.add('is-open')
        list.classList.add('is-open')
        link.setAttribute('aria-expanded', 'true')
      }
    }

    listen(link, 'click', onClick)

    onCleanup(() => {
      trigger.classList.remove('is-open')
      list.classList.remove('is-open', 'submenu-list--mobile')
      link.setAttribute('aria-expanded', 'false')
    })
  })
}

export default submenu
